import React from 'react'
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Divider,
  alpha,
} from '@mui/material'
import { format, isToday, isYesterday } from 'date-fns'
import { Transaction } from '../../types'
import TransactionCard from './TransactionCard'

interface TransactionListProps {
  transactions: Transaction[]
  onMenuClick: (event: React.MouseEvent<HTMLElement>, transaction: Transaction) => void
}

const TransactionList: React.FC<TransactionListProps> = ({ transactions, onMenuClick }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // Group transactions by date
  const groupTransactionsByDate = (transactions: Transaction[]) => {
    const groups: { [key: string]: Transaction[] } = {}
    
    transactions.forEach((transaction) => {
      try {
        const date = new Date(transaction.transactionDate)
        if (isNaN(date.getTime())) {
          // Handle invalid dates
          const key = 'Invalid Date'
          if (!groups[key]) groups[key] = []
          groups[key].push(transaction)
          return
        }
        
        let dateKey: string
        if (isToday(date)) {
          dateKey = 'Today'
        } else if (isYesterday(date)) {
          dateKey = 'Yesterday'
        } else {
          dateKey = format(date, 'EEEE, MMM dd, yyyy')
        }
        
        if (!groups[dateKey]) groups[dateKey] = []
        groups[dateKey].push(transaction)
      } catch (error) {
        // Handle any date parsing errors
        const key = 'Invalid Date'
        if (!groups[key]) groups[key] = []
        groups[key].push(transaction)
      }
    })
    
    return groups
  }

  const formatDateHeader = (dateKey: string) => {
    if (dateKey === 'Today' || dateKey === 'Yesterday' || dateKey === 'Invalid Date') {
      return dateKey
    }
    return dateKey
  }

  const calculateDayTotal = (transactions: Transaction[]) => {
    const income = transactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + t.amount, 0)
    
    const expense = transactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + t.amount, 0)
    
    return { income, expense, net: income - expense }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount)
  }

  if (!isMobile) {
    return null // This component is only for mobile
  }

  const groupedTransactions = groupTransactionsByDate(transactions)
  const dateKeys = Object.keys(groupedTransactions).sort((a, b) => {
    // Sort with Today first, Yesterday second, then by date descending
    if (a === 'Today') return -1
    if (b === 'Today') return 1
    if (a === 'Yesterday') return -1
    if (b === 'Yesterday') return 1
    if (a === 'Invalid Date') return 1
    if (b === 'Invalid Date') return -1
    
    try {
      const dateA = new Date(a)
      const dateB = new Date(b)
      return dateB.getTime() - dateA.getTime()
    } catch {
      return 0
    }
  })

  if (transactions.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          px: 3,
          textAlign: 'center',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.secondary,
            mb: 1,
          }}
        >
          No transactions found
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
          }}
        >
          Try adjusting your filters or add a new transaction
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ pb: 2 }}>
      {dateKeys.map((dateKey, index) => {
        const dayTransactions = groupedTransactions[dateKey]
        const dayTotals = calculateDayTotal(dayTransactions)
        
        return (
          <Box key={dateKey} sx={{ mb: 3 }}>
            {/* Date Header with Daily Summary */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
                px: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                {formatDateHeader(dateKey)}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {dayTotals.income > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.success.main,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                    }}
                  >
                    +{formatCurrency(dayTotals.income)}
                  </Typography>
                )}
                {dayTotals.expense > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.error.main,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                    }}
                  >
                    -{formatCurrency(dayTotals.expense)}
                  </Typography>
                )}
                {dayTotals.net !== 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: dayTotals.net > 0 ? theme.palette.success.main : theme.palette.error.main,
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      backgroundColor: alpha(
                        dayTotals.net > 0 ? theme.palette.success.main : theme.palette.error.main,
                        0.1
                      ),
                    }}
                  >
                    {dayTotals.net > 0 ? '+' : ''}{formatCurrency(dayTotals.net)}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Transactions for this date */}
            <Box sx={{ px: 1 }}>
              {dayTransactions.map((transaction) => (
                <TransactionCard
                  key={transaction.id}
                  transaction={transaction}
                  onMenuClick={onMenuClick}
                />
              ))}
            </Box>

            {/* Divider between days (except for last day) */}
            {index < dateKeys.length - 1 && (
              <Divider
                sx={{
                  mt: 2,
                  mx: 2,
                  borderColor: alpha(theme.palette.divider, 0.5),
                }}
              />
            )}
          </Box>
        )
      })}
    </Box>
  )
}

export default TransactionList